// Helper function to find the set of 10 contiguous tasks with the longest elapsed time
const findMaxContiguousDuration = subtasks => {
  const NUM_CONTIGUOUS_SUBTASKS = 10;
  let minStartTime = subtasks[0].startDate.getTime();
  let maxEndTime = subtasks[0].endDate.getTime();

  // Calculate the initial sum of the first 'windowSize' tasks
  for (let i = 1; i < NUM_CONTIGUOUS_SUBTASKS && i < subtasks.length; i++) {
    maxEndTime = Math.max(maxEndTime, subtasks[i].endDate.getTime());
  }

  let maxDuration = maxEndTime - minStartTime;

  // Slide the window across the tasks, track info, and update the maxDuration
  for (let i = NUM_CONTIGUOUS_SUBTASKS; i < subtasks.length; i++) {
    minStartTime =
      subtasks[i - NUM_CONTIGUOUS_SUBTASKS + 1].startDate.getTime();
    maxEndTime = Math.max(maxEndTime, subtasks[i].endDate.getTime());
    maxDuration = Math.max(maxDuration, maxEndTime - minStartTime);
  }

  return convertMillisecondsToDays(maxDuration);
};

// Mapping from time units to days
const UNIT_TO_DAYS = {
  Minutes: 1 / (24 * 60),
  Hour: 1 / 24,
  Day: 1,
  Week: 7,
  Month: 30,
  Year: 365,
};

// There are roughly 800 units in one screen size on my laptop
// However, its actually a little under that and this cuts off the 10th task
// Another concern is we want to have divisibility by factors 2, 4, and 8
const SCREEN_SIZE_UNITS = 720;

// Timeline settings mapping with block limits and dynamically calculated block sizes
const TIMELINE_SETTINGS = [
  { limit: 5, unit: "Minutes", count: 1 },
  { limit: 10, unit: "Minutes", count: 2 },
  { limit: 30, unit: "Minutes", count: 5 },
  { limit: 60, unit: "Minutes", count: 10 },
  { limit: 120, unit: "Minutes", count: 20 },
  { limit: 4, unit: "Hour", count: 1 },
  { limit: 8, unit: "Hour", count: 1 },
  { limit: 24, unit: "Hour", count: 4 },
  { limit: 2, unit: "Day", count: 1 },
  { limit: 4, unit: "Day", count: 1 },
  { limit: 8, unit: "Day", count: 1 },
  { limit: 2, unit: "Week", count: 1 },
  { limit: 5, unit: "Week", count: 1 },
  { limit: 2, unit: "Month", count: 1 },
  { limit: 4, unit: "Month", count: 1 },
  { limit: 12, unit: "Month", count: 2 },
  { limit: 2, unit: "Year", count: 1 },
  { limit: 4, unit: "Year", count: 1 },
  { limit: 8, unit: "Year", count: 1 },
].map(setting => ({
  ...setting,
  limitInDays: setting.limit * UNIT_TO_DAYS[setting.unit],
  blockSize: (SCREEN_SIZE_UNITS * setting.count) / setting.limit,
}));

const createTimelineSettingObject = setting => {
  const formatter = date => {
    switch (setting.unit) {
      case "Minutes":
        const totalMinutes = date.getHours() * 60 + date.getMinutes();
        return `${totalMinutes}m`;
      case "Hour":
        return `${date.getHours()}h`;
      case "Day":
        return date.toLocaleDateString(undefined, { weekday: "short" });
      case "Week":
        return date.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      case "Month":
        return date.toLocaleDateString(undefined, { month: "short" });
      default:
        return `${date.getFullYear()}`;
    }
  };

  return {
    topTier: {
      unit: setting.unit,
      count: setting.count,
      formatter: formatter,
    },
    timelineUnitSize: setting.blockSize,
  };
};

export const determineTimelineSettings = subtasks => {
  // Check if subtasks array is null or empty and return the last setting immediately
  const lastSetting = TIMELINE_SETTINGS[TIMELINE_SETTINGS.length - 1];
  if (subtasks.length === 0) {
    return {
      timelineSettings: createTimelineSettingObject(lastSetting),
      minProjectDurationMins: 0,
    };
  }

  const maxContiguousDuration = findMaxContiguousDuration(subtasks);

  // Early exit with the last setting if maxContiguousDuration is beyond the second-to-last setting
  const secondToLastSetting = TIMELINE_SETTINGS[TIMELINE_SETTINGS.length - 2];
  if (maxContiguousDuration > secondToLastSetting.limitInDays) {
    return {
      timelineSettings: createTimelineSettingObject(lastSetting),
      minProjectDurationMins: convertDaysToMins(lastSetting.limitInDays),
    };
  }

  let low = 0;
  let high = TIMELINE_SETTINGS.length - 2; // Exclude the last setting from binary search
  let bestMatch = null;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const setting = TIMELINE_SETTINGS[mid];

    if (maxContiguousDuration <= setting.limitInDays) {
      bestMatch = setting; // Store the current setting as a possible best match
      high = mid - 1; // Try to find a smaller limit that still satisfies the condition
    } else {
      low = mid + 1; // Move to higher settings
    }
  }

  return {
    timelineSettings: createTimelineSettingObject(bestMatch),
    minProjectDurationMins: convertDaysToMins(bestMatch.limitInDays),
  };
};

export const convertMinsToDays = mins => {
  return mins / (24 * 60);
};

export const convertDaysToMins = days => {
  return days * (24 * 60);
};

export const convertMillisecondsToDays = ms => {
  return ms / (1000 * 60 * 24 * 60);
};

export const timeToMinutes = timeDict => {
  let total = 0;
  for (const key in timeDict) {
    const value = timeDict[key];
    if (value === 0) {
      continue;
    }
    switch (key) {
      case "years":
        total += value * 60 * 24 * 365;
        break;
      case "months":
        total += value * 60 * 24 * 30;
        break;
      case "weeks":
        total += value * 60 * 24 * 7;
        break;
      case "days":
        total += value * 60 * 24;
        break;
      case "hours":
        total += value * 60;
        break;
      case "minutes":
        total += value;
        break;
      default:
        console.error(`Invalid time unit: ${key}`); // Handle unexpected units
        break;
    }
  }

  return total;
};

const timeUnits = [
  ["y", 525600],
  ["mo", 43200],
  ["w", 10080],
  ["d", 1440],
  ["h", 60],
  ["m", 1],
];

// Converts minutes into a formatted string showing only the
// two largest consecutive time units where the first unit is non-zero
export const getFormattedDurationFromMinutes = minutes => {
  let firstUnitValue = 0;
  let result = "";

  for (const [unit, value] of timeUnits) {
    const unitValue = Math.floor(minutes / value);
    minutes %= value; // Reduce minutes for next unit calculation

    if (!firstUnitValue) {
      if (unitValue === 0) continue; // Skip zero values until the first non-zero unit is found
      firstUnitValue = unitValue; // Record the first non-zero unit found
      result = `${unitValue}${unit}`;
    } else {
      // Once the first non-zero unit is found, check the next unit
      if (unitValue > 0) {
        result += ` ${unitValue}${unit}`;
      }
      break; // Exit after the second unit is processed (whether zero or not)
    }
  }

  return result;
};
